import React, { useState, useEffect } from 'react';
import { MapPin, Tag, Edit2, X, Check, Store, Utensils, DoorOpen, ChevronDown, ChevronUp, Plus, Search, Star, Lock, FileText } from 'lucide-react';

const TypeBadge = ({ type }) => {
  const config = {
    retail: { bg: '#eff6ff', color: '#3b82f6', label: 'Retail', icon: Store },
    concession: { bg: '#f0fdf4', color: '#10b981', label: 'Concession', icon: Utensils },
    gate: { bg: '#fef3c7', color: '#f59e0b', label: 'Gate', icon: DoorOpen }
  };
  const { bg, color, label, icon: Icon } = config[type] || config.concession;
  return (
    <span style={{ background: bg, color, padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <Icon size={14} />{label}
    </span>
  );
};

const StatusBadge = ({ enabled }) => (
  <span style={{ background: enabled ? '#f0fdf4' : '#fef2f2', color: enabled ? '#10b981' : '#dc3545', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase' }}>
    {enabled ? 'Enabled' : 'Disabled'}
  </span>
);

// Reusable description display + edit trigger.
// Renders regardless of whether `description` exists — falls back to a
// placeholder when the field is absent (e.g. Braves docs have no description yet).
const DescriptionBlock = ({ description, onEdit, canEdit }) => (
  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
        <FileText size={14} />
        <span style={{ fontSize: '13px', fontWeight: '600', textTransform: 'uppercase' }}>Description</span>
      </div>
      <button onClick={onEdit} style={{ background: '#53b8aa', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: 'white', fontWeight: '500', fontSize: '13px', opacity: canEdit ? 1 : 0.7 }}>
        <Edit2 size={14} /> Edit
      </button>
    </div>
    <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5', margin: 0 }}>
      {description ? description : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No description set</span>}
    </p>
  </div>
);

const AccessDeniedModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '90%', maxWidth: '420px', textAlign: 'center' }}>
        <div style={{ 
          width: '64px', 
          height: '64px', 
          background: '#fef2f2', 
          borderRadius: '50%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          margin: '0 auto 20px' 
        }}>
          <Lock size={32} style={{ color: '#dc3545' }} />
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 12px 0' }}>
          Edit Access Required
        </h2>
        <p style={{ fontSize: '15px', color: '#6b7280', margin: '0 0 24px 0', lineHeight: '1.6' }}>
          You do not have permission to edit Ballpark locations. Please contact your administrator to request edit access.
        </p>
        <button 
          onClick={onClose} 
          style={{ 
            background: '#53b8aa', 
            color: 'white', 
            border: 'none', 
            borderRadius: '10px', 
            padding: '12px 32px', 
            cursor: 'pointer', 
            fontWeight: '600',
            fontSize: '15px'
          }}
        >
          Got It
        </button>
      </div>
    </div>
  );
};

const TagEditorModal = ({ isOpen, onClose, location, section, tags, onSave }) => {
  const [editedTags, setEditedTags] = useState([]);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (isOpen) { setEditedTags(tags || []); setNewTag(''); }
  }, [isOpen, tags]);

  if (!isOpen) return null;

  const handleAddTag = () => {
    if (newTag.trim() && !editedTags.includes(newTag.trim())) {
      setEditedTags([...editedTags, newTag.trim()]);
      setNewTag('');
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Edit Tags</h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>{location} {section ? `• ${section}` : ''}</p>
          </div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '10px', padding: '10px', cursor: 'pointer' }}><X size={20} style={{ color: '#6b7280' }} /></button>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          <input type="text" value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} placeholder="Add a new tag..." style={{ flex: 1, padding: '12px 16px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '15px', outline: 'none' }} />
          <button onClick={handleAddTag} style={{ background: '#53b8aa', color: 'white', border: 'none', borderRadius: '10px', padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}><Plus size={18} /> Add</button>
        </div>
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', fontWeight: '600' }}>{editedTags.length} Tags</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {editedTags.map((tag, idx) => (
              <span key={idx} style={{ background: 'white', border: '2px solid #e5e7eb', padding: '8px 12px', borderRadius: '8px', fontSize: '14px', color: '#374151', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                {tag}
                <button onClick={() => setEditedTags(editedTags.filter(t => t !== tag))} style={{ background: '#fef2f2', border: 'none', borderRadius: '4px', padding: '2px', cursor: 'pointer' }}><X size={14} style={{ color: '#dc3545' }} /></button>
              </span>
            ))}
            {editedTags.length === 0 && <span style={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}>No tags added yet</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '10px', padding: '12px 24px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
          <button onClick={() => { onSave(editedTags); onClose(); }} style={{ background: '#53b8aa', color: 'white', border: 'none', borderRadius: '10px', padding: '12px 24px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}><Check size={18} /> Save Changes</button>
        </div>
      </div>
    </div>
  );
};

const DescriptionEditorModal = ({ isOpen, onClose, location, section, description, onSave }) => {
  const [editedDescription, setEditedDescription] = useState('');

  useEffect(() => {
    if (isOpen) { setEditedDescription(description || ''); }
  }, [isOpen, description]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Edit Description</h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>{location} {section ? `• ${section}` : ''}</p>
          </div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '10px', padding: '10px', cursor: 'pointer' }}><X size={20} style={{ color: '#6b7280' }} /></button>
        </div>
        <textarea
          value={editedDescription}
          onChange={(e) => setEditedDescription(e.target.value)}
          placeholder="Enter a description for this location..."
          rows={6}
          style={{ width: '100%', padding: '14px 16px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '15px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5', boxSizing: 'border-box' }}
        />
        <div style={{ fontSize: '13px', color: '#9ca3af', margin: '8px 0 24px 0', textAlign: 'right' }}>{editedDescription.length} characters</div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '10px', padding: '12px 24px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
          <button onClick={() => { onSave(editedDescription); onClose(); }} style={{ background: '#53b8aa', color: 'white', border: 'none', borderRadius: '10px', padding: '12px 24px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}><Check size={18} /> Save Changes</button>
        </div>
      </div>
    </div>
  );
};

const RetailCard = ({ location, onEditTags, onEditDescription, onToggleFeatured, canEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [showTags, setShowTags] = useState(false);

  return (
    <div style={{ background: 'white', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: location.isFeatured ? '2px solid #f59e0b' : '2px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {location.imageUrl && (
          <div style={{ width: '100px', height: '100px', background: '#f3f4f6', flexShrink: 0, borderRadius: '12px', margin: '16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={location.imageUrl} alt={location.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
          </div>
        )}
        <div style={{ flex: 1, padding: location.imageUrl ? '16px 16px 16px 0' : '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 8px 0' }}>{location.name}</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <TypeBadge type={location.type} />
                <StatusBadge enabled={location.enabled} />
                {location.isFeatured && <span style={{ background: '#fef3c7', color: '#f59e0b', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Star size={12} fill="#f59e0b" /> Featured</span>}
              </div>
            </div>
            <button onClick={() => onToggleFeatured(location.locationId)} style={{ background: location.isFeatured ? '#fef3c7' : '#f3f4f6', border: location.isFeatured ? '2px solid #f59e0b' : '2px solid #e5e7eb', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: location.isFeatured ? '#b45309' : '#6b7280', fontWeight: '600', fontSize: '13px', opacity: canEdit ? 1 : 0.7 }}>
              <Star size={16} fill={location.isFeatured ? '#f59e0b' : 'none'} color={location.isFeatured ? '#f59e0b' : '#6b7280'} />
              {location.isFeatured ? 'Featured' : 'Feature'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#6b7280' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><MapPin size={14} />{location.subtitle || location.location}</div>
          </div>
        </div>
      </div>

      <button onClick={() => setExpanded(!expanded)} style={{ width: '100%', background: '#f8fafc', border: 'none', borderTop: '1px solid #e5e7eb', padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#374151', fontWeight: '500', fontSize: '14px' }}>
        <span>{expanded ? 'Hide' : 'Show'} Details</span>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {expanded && (
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginTop: '12px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MapPin size={16} style={{ color: '#6b7280' }} />
                <div>
                  <div style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>{location.subtitle || location.location}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {location.tags && (
                  <>
                    <button onClick={() => setShowTags(!showTags)} style={{ background: '#eff6ff', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#3b82f6', fontWeight: '500', fontSize: '13px' }}>
                      <Tag size={14} />{location.tags.length} Tags {showTags ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    <button onClick={() => onEditTags(location.name, null, location.tags, location.locationId, null, null)} style={{ background: '#53b8aa', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: 'white', fontWeight: '500', fontSize: '13px', opacity: canEdit ? 1 : 0.7 }}>
                      <Edit2 size={14} /> Edit
                    </button>
                  </>
                )}
              </div>
            </div>
            {showTags && location.tags && location.tags.length > 0 && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>{location.tags.map((tag, idx) => <span key={idx} style={{ background: 'white', border: '1px solid #d1d5db', padding: '4px 10px', borderRadius: '6px', fontSize: '13px', color: '#4b5563' }}>{tag}</span>)}</div>
              </div>
            )}
            <DescriptionBlock
              description={location.description}
              onEdit={() => onEditDescription(location.name, null, location.description, location.locationId, null, null)}
              canEdit={canEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ItemCard = ({ item, itemType, locationName, locationId, onEditTags, onEditDescription, onToggleFeatured, canEdit }) => {
  const [showTags, setShowTags] = useState(false);
  const itemId = itemType === 'section' ? item.sectionId : item.gateId;
  const itemLabel = itemType === 'section' ? `Section ${item.sectionId}` : item.label;

  return (
    <div style={{ background: item.isFeatured ? '#fffbeb' : '#f8fafc', borderRadius: '12px', padding: '16px', marginTop: '12px', border: item.isFeatured ? '2px solid #f59e0b' : '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {itemType === 'section' ? <MapPin size={16} style={{ color: '#6b7280' }} /> : <DoorOpen size={16} style={{ color: '#f59e0b' }} />}
          <div>
            <div style={{ fontWeight: '600', color: '#374151', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>{itemLabel}{item.isFeatured && <Star size={14} fill="#f59e0b" color="#f59e0b" />}</div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>{item.subtitle}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <StatusBadge enabled={item.enabled} />
          <button onClick={() => onToggleFeatured(locationId, itemId, itemType)} style={{ background: item.isFeatured ? '#fef3c7' : '#f3f4f6', border: item.isFeatured ? '2px solid #f59e0b' : '2px solid #e5e7eb', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: item.isFeatured ? '#b45309' : '#6b7280', fontWeight: '500', fontSize: '12px', opacity: canEdit ? 1 : 0.7 }}>
            <Star size={14} fill={item.isFeatured ? '#f59e0b' : 'none'} color={item.isFeatured ? '#f59e0b' : '#6b7280'} />{item.isFeatured ? 'Featured' : 'Feature'}
          </button>
          <button onClick={() => setShowTags(!showTags)} style={{ background: '#eff6ff', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#3b82f6', fontWeight: '500', fontSize: '13px' }}><Tag size={14} />{item.tags?.length || 0} Tags {showTags ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
          <button onClick={() => onEditTags(locationName, item.subtitle || itemLabel, item.tags, locationId, itemId, itemType)} style={{ background: '#53b8aa', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: 'white', fontWeight: '500', fontSize: '13px', opacity: canEdit ? 1 : 0.7 }}><Edit2 size={14} /> Edit</button>
        </div>
      </div>
      {showTags && item.tags && item.tags.length > 0 && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>{item.tags.map((tag, idx) => <span key={idx} style={{ background: 'white', border: '1px solid #d1d5db', padding: '4px 10px', borderRadius: '6px', fontSize: '13px', color: '#4b5563' }}>{tag}</span>)}</div>
        </div>
      )}
      <DescriptionBlock
        description={item.description}
        onEdit={() => onEditDescription(locationName, item.subtitle || itemLabel, item.description, locationId, itemId, itemType)}
        canEdit={canEdit}
      />
    </div>
  );
};

const FeaturedItemCard = ({ item, itemType, locationName, locationId, type, onEditTags, onEditDescription, onToggleFeatured, canEdit }) => {
  const [showTags, setShowTags] = useState(false);
  const itemId = itemType === 'section' ? item.sectionId : item.gateId;
  const itemLabel = itemType === 'section' ? `Section ${item.sectionId}` : item.label;

  return (
    <div style={{ background: 'white', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '2px solid #f59e0b' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {item.imageUrl && (
          <div style={{ width: '100px', height: '100px', background: '#f3f4f6', flexShrink: 0, borderRadius: '12px', margin: '16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={item.imageUrl} alt={locationName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
          </div>
        )}
        <div style={{ flex: 1, padding: item.imageUrl ? '16px 16px 16px 0' : '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 4px 0' }}>{locationName}</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>{itemLabel} • {item.subtitle}</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <TypeBadge type={type} />
                <StatusBadge enabled={item.enabled} />
                <span style={{ background: '#fef3c7', color: '#f59e0b', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Star size={12} fill="#f59e0b" /> Featured</span>
              </div>
            </div>
            <button onClick={() => onToggleFeatured(locationId, itemId, itemType)} style={{ background: '#fef3c7', border: '2px solid #f59e0b', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#b45309', fontWeight: '600', fontSize: '13px', opacity: canEdit ? 1 : 0.7 }}>
              <Star size={16} fill="#f59e0b" color="#f59e0b" />Featured
            </button>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <button onClick={() => setShowTags(!showTags)} style={{ background: '#eff6ff', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#3b82f6', fontWeight: '500', fontSize: '13px' }}><Tag size={14} />{item.tags?.length || 0} Tags {showTags ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
              <button onClick={() => onEditTags(locationName, item.subtitle || itemLabel, item.tags, locationId, itemId, itemType)} style={{ background: '#53b8aa', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: 'white', fontWeight: '500', fontSize: '13px', opacity: canEdit ? 1 : 0.7 }}><Edit2 size={14} /> Edit Tags</button>
            </div>
            {showTags && item.tags && item.tags.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{item.tags.map((tag, idx) => <span key={idx} style={{ background: '#f3f4f6', border: '1px solid #d1d5db', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', color: '#4b5563' }}>{tag}</span>)}</div>}
            <DescriptionBlock
              description={item.description}
              onEdit={() => onEditDescription(locationName, item.subtitle || itemLabel, item.description, locationId, itemId, itemType)}
              canEdit={canEdit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ConcessionCard = ({ location, onEditTags, onEditDescription, onToggleFeatured, canEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const featuredCount = location.sections?.filter(s => s.isFeatured).length || 0;

  return (
    <div style={{ background: 'white', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '2px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {location.sections?.[0]?.imageUrl && (
          <div style={{ width: '100px', height: '100px', background: '#f3f4f6', flexShrink: 0, borderRadius: '12px', margin: '16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={location.sections[0].imageUrl} alt={location.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
          </div>
        )}
        <div style={{ flex: 1, padding: location.sections?.[0]?.imageUrl ? '16px 16px 16px 0' : '16px' }}>
          <div style={{ marginBottom: '12px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 8px 0' }}>{location.name}</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <TypeBadge type={location.type} />
              {featuredCount > 0 && <span style={{ background: '#fef3c7', color: '#f59e0b', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Star size={12} fill="#f59e0b" /> {featuredCount} Featured</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#6b7280' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><MapPin size={14} />{location.sections?.length || 0} {(location.sections?.length || 0) === 1 ? 'Section' : 'Sections'}</div>
          </div>
        </div>
      </div>
      <button onClick={() => setExpanded(!expanded)} style={{ width: '100%', background: '#f8fafc', border: 'none', borderTop: '1px solid #e5e7eb', padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#374151', fontWeight: '500', fontSize: '14px' }}>
        <span>{expanded ? 'Hide' : 'Show'} Sections ({location.sections?.length || 0})</span>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {expanded && (
        <div style={{ padding: '0 20px 20px' }}>
          {location.sections?.map((section, idx) => <ItemCard key={idx} item={section} itemType="section" locationName={location.name} locationId={location.locationId} onEditTags={onEditTags} onEditDescription={onEditDescription} onToggleFeatured={onToggleFeatured} canEdit={canEdit} />)}
        </div>
      )}
    </div>
  );
};

const GateCard = ({ location, onEditTags, onEditDescription, onToggleFeatured, canEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const featuredCount = location.gates?.filter(g => g.isFeatured).length || 0;

  return (
    <div style={{ background: 'white', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '2px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {location.gates?.[0]?.imageUrl && (
          <div style={{ width: '100px', height: '100px', background: '#f3f4f6', flexShrink: 0, borderRadius: '12px', margin: '16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={location.gates[0].imageUrl} alt={location.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
          </div>
        )}
        <div style={{ flex: 1, padding: location.gates?.[0]?.imageUrl ? '16px 16px 16px 0' : '16px' }}>
          <div style={{ marginBottom: '12px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 8px 0' }}>{location.name}</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <TypeBadge type={location.type} />
              {featuredCount > 0 && <span style={{ background: '#fef3c7', color: '#f59e0b', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Star size={12} fill="#f59e0b" /> {featuredCount} Featured</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#6b7280' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><DoorOpen size={14} />{location.gates?.length || 0} {(location.gates?.length || 0) === 1 ? 'Gate' : 'Gates'}</div>
          </div>
        </div>
      </div>
      <button onClick={() => setExpanded(!expanded)} style={{ width: '100%', background: '#f8fafc', border: 'none', borderTop: '1px solid #e5e7eb', padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#374151', fontWeight: '500', fontSize: '14px' }}>
        <span>{expanded ? 'Hide' : 'Show'} Gates ({location.gates?.length || 0})</span>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {expanded && (
        <div style={{ padding: '0 20px 20px' }}>
          {location.gates?.map((gate, idx) => <ItemCard key={idx} item={gate} itemType="gate" locationName={location.name} locationId={location.locationId} onEditTags={onEditTags} onEditDescription={onEditDescription} onToggleFeatured={onToggleFeatured} canEdit={canEdit} />)}
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, count, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', marginTop: '32px' }}>
    <div style={{ background: color, borderRadius: '10px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={20} color="white" /></div>
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>{title}</h2>
      <p style={{ fontSize: '14px', color: '#6b7280', margin: '2px 0 0 0' }}>{count} {count === 1 ? 'item' : 'items'}</p>
    </div>
  </div>
);

const BallparkTabView = ({ ballparkAccess, ballparkLocations, toggleFeatured, updateTags, updateDescription }) => {
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [descModalOpen, setDescModalOpen] = useState(false);
  const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);
  const [editingContext, setEditingContext] = useState({ location: '', section: '', tags: [] });
  const [editingDescContext, setEditingDescContext] = useState({ location: '', section: '', description: '', locationId: null, itemId: null, itemType: null });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (ballparkLocations && ballparkLocations.length > 0) {
      setLocations(ballparkLocations);
    }
  }, [ballparkLocations]);

  const canEdit = ballparkAccess?.userBallparkEditEnabled === true;

  const showToast = (message, success = true) => {
    setToast({ message, success });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEditTags = (locationName, sectionName, tags, locationId, itemId, itemType) => {
    if (!canEdit) {
      setAccessDeniedOpen(true);
      return;
    }
    setEditingContext({ location: locationName, section: sectionName, tags: tags || [], locationId, itemId: itemId || null, itemType: itemType || null });
    setModalOpen(true);
  };

  const handleEditDescription = (locationName, sectionName, description, locationId, itemId, itemType) => {
    if (!canEdit) {
      setAccessDeniedOpen(true);
      return;
    }
    setEditingDescContext({ location: locationName, section: sectionName, description: description || '', locationId, itemId: itemId || null, itemType: itemType || null });
    setDescModalOpen(true);
  };

  const handleToggleFeatured = (locationId, itemId = null, itemType = null) => {
    if (!canEdit) {
      setAccessDeniedOpen(true);
      return;
    }
    setLocations(prev => prev.map(loc => {
      if (loc.locationId === locationId) {
        if (itemId === null) {
          toggleFeatured(loc.locationId, null, null, !loc.isFeatured, (success) => {
            showToast(success ? 'Featured status updated' : 'Failed to update featured status', success);
          });
          return { ...loc, isFeatured: !loc.isFeatured };
        }
        if (itemType === 'section' && loc.sections) {
          const section = loc.sections.find(s => s.sectionId === itemId);
          if (section) {
            toggleFeatured(loc.locationId, itemId, 'section', !section.isFeatured, (success) => {
              showToast(success ? 'Featured status updated' : 'Failed to update featured status', success);
            });
          }
          return { ...loc, sections: loc.sections.map(s => s.sectionId === itemId ? { ...s, isFeatured: !s.isFeatured } : s) };
        }
        if (itemType === 'gate' && loc.gates) {
          const gate = loc.gates.find(g => g.gateId === itemId);
          if (gate) {
            toggleFeatured(loc.locationId, itemId, 'gate', !gate.isFeatured, (success) => {
              showToast(success ? 'Featured status updated' : 'Failed to update featured status', success);
            });
          }
          return { ...loc, gates: loc.gates.map(g => g.gateId === itemId ? { ...g, isFeatured: !g.isFeatured } : g) };
        }
      }
      return loc;
    }));
  };

  const handleSaveTags = (newTags) => {
    const { locationId, itemId, itemType } = editingContext;
    updateTags(locationId, itemId, itemType, newTags, (success) => {
      showToast(success ? 'Tags updated successfully' : 'Failed to update tags', success);
    });

    setLocations(prev => prev.map(loc => {
      if (loc.locationId === locationId) {
        if (!itemId) return { ...loc, tags: newTags };
        if (itemType === 'section' && loc.sections) return { ...loc, sections: loc.sections.map(s => s.sectionId === itemId ? { ...s, tags: newTags } : s) };
        if (itemType === 'gate' && loc.gates) return { ...loc, gates: loc.gates.map(g => g.gateId === itemId ? { ...g, tags: newTags } : g) };
      }
      return loc;
    }));
  };

  const handleSaveDescription = (newDescription) => {
    const { locationId, itemId, itemType } = editingDescContext;
    updateDescription(locationId, itemId, itemType, newDescription, (success) => {
      showToast(success ? 'Description updated successfully' : 'Failed to update description', success);
    });

    setLocations(prev => prev.map(loc => {
      if (loc.locationId === locationId) {
        if (!itemId) return { ...loc, description: newDescription };
        if (itemType === 'section' && loc.sections) return { ...loc, sections: loc.sections.map(s => s.sectionId === itemId ? { ...s, description: newDescription } : s) };
        if (itemType === 'gate' && loc.gates) return { ...loc, gates: loc.gates.map(g => g.gateId === itemId ? { ...g, description: newDescription } : g) };
      }
      return loc;
    }));
  };

  const getFeaturedItems = () => {
    const featured = [];
    locations.forEach(loc => {
      if (!loc.name.toLowerCase().includes(searchTerm.toLowerCase()) && searchTerm !== '') return;
      if (loc.type === 'retail' && loc.isFeatured) featured.push({ type: 'retail', location: loc });
      if (loc.sections) loc.sections.filter(s => s.isFeatured).forEach(s => featured.push({ type: 'concession', location: loc, item: s, itemType: 'section' }));
      if (loc.gates) loc.gates.filter(g => g.isFeatured).forEach(g => featured.push({ type: 'gate', location: loc, item: g, itemType: 'gate' }));
    });
    return featured;
  };

  const filtered = locations.filter(loc => loc.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const retailLocations = filtered.filter(loc => loc.type === 'retail' && !loc.isFeatured);
  const concessionLocations = filtered.filter(loc => loc.type === 'concession');
  const gateLocations = filtered.filter(loc => loc.type === 'gate');
  const featuredItems = getFeaturedItems();

  const countFeatured = () => {
    let c = 0;
    locations.forEach(loc => {
      if (loc.type === 'retail' && loc.isFeatured) c++;
      if (loc.sections) c += loc.sections.filter(s => s.isFeatured).length;
      if (loc.gates) c += loc.gates.filter(g => g.isFeatured).length;
    });
    return c;
  };

  const typeCounts = { all: locations.length, featured: countFeatured(), retail: locations.filter(l => l.type === 'retail').length, concession: locations.filter(l => l.type === 'concession').length, gate: locations.filter(l => l.type === 'gate').length };

  const showFeatured = (filterType === 'all' || filterType === 'featured') && featuredItems.length > 0;
  const showRetail = (filterType === 'all' || filterType === 'retail') && retailLocations.length > 0;
  const showConcessions = (filterType === 'all' || filterType === 'concession') && concessionLocations.length > 0;
  const showGates = (filterType === 'all' || filterType === 'gate') && gateLocations.length > 0;
  const noResults = !showFeatured && !showRetail && !showConcessions && !showGates;

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 8px 0' }}>Ballpark Locations</h1>
            <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>Manage all locations shown in the MLB Ballpark app. Changes take 1 minute to appear in app.</p>
          </div>
          {!canEdit && (
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #f59e0b', 
              borderRadius: '10px', 
              padding: '10px 16px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '14px',
              color: '#b45309'
            }}>
              <Lock size={16} />
              <span>View Only Mode</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '250px' }}>
            <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search locations..." style={{ width: '100%', padding: '12px 16px 12px 44px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '15px', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[{ key: 'all', label: 'All', color: '#53b8aa' }, { key: 'featured', label: 'Featured', color: '#f59e0b' }, { key: 'retail', label: 'Retail', color: '#3b82f6' }, { key: 'concession', label: 'Concessions', color: '#10b981' }, { key: 'gate', label: 'Gates', color: '#f59e0b' }].map(({ key, label, color }) => (
              <button key={key} onClick={() => setFilterType(key)} style={{ background: filterType === key ? color : '#f3f4f6', color: filterType === key ? 'white' : '#374151', border: 'none', borderRadius: '10px', padding: '10px 18px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {key === 'featured' && <Star size={14} fill={filterType === key ? 'white' : 'none'} />}{label} ({typeCounts[key]})
              </button>
            ))}
          </div>
        </div>
      </div>

      {showFeatured && (
        <>
          <SectionHeader icon={Star} title="Featured" count={featuredItems.length} color="#f59e0b" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '20px', alignItems: 'start' }}>
            {featuredItems.map((item, idx) => item.type === 'retail' ? <RetailCard key={`r-${item.location.locationId}`} location={item.location} onEditTags={handleEditTags} onEditDescription={handleEditDescription} onToggleFeatured={handleToggleFeatured} canEdit={canEdit} /> : <FeaturedItemCard key={`${item.itemType}-${item.location.locationId}-${item.item.sectionId || item.item.gateId}`} item={item.item} itemType={item.itemType} locationName={item.location.name} locationId={item.location.locationId} type={item.type} onEditTags={handleEditTags} onEditDescription={handleEditDescription} onToggleFeatured={handleToggleFeatured} canEdit={canEdit} />)}
          </div>
        </>
      )}

      {showRetail && (
        <>
          <SectionHeader icon={Store} title="Retail" count={retailLocations.length} color="#3b82f6" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '20px', alignItems: 'start' }}>
            {retailLocations.map(loc => <RetailCard key={loc.locationId} location={loc} onEditTags={handleEditTags} onEditDescription={handleEditDescription} onToggleFeatured={handleToggleFeatured} canEdit={canEdit} />)}
          </div>
        </>
      )}

      {showConcessions && (
        <>
          <SectionHeader icon={Utensils} title="Concessions" count={concessionLocations.length} color="#10b981" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '20px', alignItems: 'start' }}>
            {concessionLocations.map(loc => <ConcessionCard key={loc.locationId} location={loc} onEditTags={handleEditTags} onEditDescription={handleEditDescription} onToggleFeatured={handleToggleFeatured} canEdit={canEdit} />)}
          </div>
        </>
      )}

      {showGates && (
        <>
          <SectionHeader icon={DoorOpen} title="Gates" count={gateLocations.length} color="#f59e0b" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '20px', alignItems: 'start' }}>
            {gateLocations.map(loc => <GateCard key={loc.locationId} location={loc} onEditTags={handleEditTags} onEditDescription={handleEditDescription} onToggleFeatured={handleToggleFeatured} canEdit={canEdit} />)}
          </div>
        </>
      )}

      {noResults && (
        <div style={{ background: 'white', borderRadius: '18px', padding: '60px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          <Search size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', color: '#6b7280', margin: 0 }}>No locations found</h3>
          <p style={{ fontSize: '14px', color: '#9ca3af', margin: '8px 0 0 0' }}>Try adjusting your search or filters</p>
        </div>
      )}

      <TagEditorModal isOpen={modalOpen} onClose={() => setModalOpen(false)} location={editingContext.location} section={editingContext.section} tags={editingContext.tags} onSave={handleSaveTags} />
      <DescriptionEditorModal isOpen={descModalOpen} onClose={() => setDescModalOpen(false)} location={editingDescContext.location} section={editingDescContext.section} description={editingDescContext.description} onSave={handleSaveDescription} />
      <AccessDeniedModal isOpen={accessDeniedOpen} onClose={() => setAccessDeniedOpen(false)} />

      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: toast.success ? '#10b981' : '#dc3545',
          color: 'white',
          padding: '14px 24px',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          fontSize: '15px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 1001,
          animation: 'slideIn 0.3s ease-out'
        }}>
          <span>{toast.success ? '✓' : '✕'}</span>
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default BallparkTabView;